import { routePaths } from "@/App";
import { TagBadge } from "@/components/TagBadge";
import { Button } from "@/components/ui/button";
import { IconInfoCircle } from "@tabler/icons-react";
import { ChevronLeft, MapPin, UserIcon } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router";
import { TourTaskItem } from "../components/TourTaskItem";
import { useTourDetailContext } from "../data/provider";
import { useTourStop } from "../hooks/useTourStop";

import { NavigationSelect } from "@/components/NavigationSelect";
import { useTranslation } from "react-i18next";
import { useTourNavigation } from "../hooks/useTourNavigation";

interface DeliveryViewProps {
  tourId: string;
  stopId: string;
}

export function DeliveryView({ tourId, stopId }: DeliveryViewProps) {
  const { t } = useTranslation();

  const { tour } = useTourDetailContext();

  const { currentStop } = useTourStop(stopId);

  const { navigateToNextStop } = useTourNavigation(currentStop);

  const navigate = useNavigate();

  const handleTourTaskClick = (taskId: string) => {
    navigate(`/tour/${tour.id}/details/${stopId}/${taskId}`);
  };

  if (!currentStop) {
    return <Navigate to={routePaths.createTourPath(tourId)} />;
  }

  return (
    <div className="h-dvh">
      <div className="h-full flex flex-col">
        <div className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
          <Link
            to={routePaths.createTourPath(tourId)}
            className="mb-6 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
            {t("common.back")}
          </Link>

          <div className="space-y-2">
            <h3 className="text-xl font-semibold">
              {t("orderDelivery.header.title")}
            </h3>
            <div className="space-y-2 mt-4">
              {currentStop.items.length === 1 ? (
                <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                  <UserIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {currentStop.items[0].name}
                </div>
              ) : (
                <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                  <UserIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {t("tourStopCard.multipleRecipients")}
                </div>
              )}

              <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                <MapPin
                  className="w-5 h-5 mr-2 flex-shrink-0"
                  strokeWidth={2.5}
                />
                <span>{currentStop.address}</span>
              </div>

              {currentStop.aditionalAddress && (
                <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                  <IconInfoCircle
                    className="w-5 h-5 mr-2 flex-shrink-0"
                    strokeWidth={2.5}
                  />
                  {currentStop.aditionalAddress}
                </div>
              )}

              {!!currentStop.tags?.length && (
                <div className="flex gap-2 mt-6 flex-wrap">
                  {currentStop.tags.map((tag) => (
                    <TagBadge key={tag.id} {...tag} />
                  ))}
                </div>
              )}

              <NavigationSelect
                address={currentStop.address}
                vehicle={tour.vehicle}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4 overflow-y-scroll flex-1 bg-list-background">
          {currentStop.items.map((item) => (
            <TourTaskItem
              key={item.id}
              item={item}
              onClick={() => {
                handleTourTaskClick(item.id);
              }}
            />
          ))}
        </div>

        <footer className="p-4 bg-white border-t-2 border-neutral pb-safe">
          <Button
            className="w-full"
            variant="secondary"
            onClick={navigateToNextStop}
          >
            {t("common.nextStop")}
          </Button>
        </footer>
      </div>
    </div>
  );
}
